import { Image as ImageLucide } from 'lucide-react'
import { useUsageSnapshot } from '../PlanMeter'
import { FILE_MAX_MB, detectFileKind, fileBadgeLabel } from './fileKind'

/**
 * Mobile-only guest/free conversion strip. The history ContextPanel carries
 * the equivalent usage meter, but it's hidden on mobile, so mobile guests
 * otherwise have no visible upgrade surface. Hidden for Pro.
 */
export function MobileUsageStrip({
  isAuthed,
  isPro,
  onAction,
}: {
  isAuthed: boolean
  isPro: boolean
  onAction: () => void
}) {
  const { left } = useUsageSnapshot(isAuthed)
  if (isPro) return null
  return (
    <div className="md:hidden flex items-center justify-between gap-3 px-4 pt-3 min-w-0 overflow-hidden">
      {/* `min-w-0` lets the mono counter actually truncate. Without it a flex
        * item's automatic minimum is its full nowrap width, so a slightly wider
        * font pushed the CTA off the right edge of the phone. */}
      <span className="font-mono tabular-nums truncate min-w-0 text-[11px]" style={{ color: 'var(--muted-2)' }}>
        {left.toLocaleString()} free characters left
      </span>
      {/* A text link, not a button: the one terracotta control on this screen
        * is Translate. */}
      <button
        type="button"
        onClick={onAction}
        className="touch-44-y shrink-0 inline-flex items-center text-[13px] font-semibold whitespace-nowrap cursor-pointer"
        style={{ color: 'var(--accent-deep)', background: 'transparent', border: 0, padding: 0 }}
      >
        {isAuthed ? 'Upgrade to Pro' : 'Sign In Free'}
      </button>
    </div>
  )
}

/**
 * The picked file preview is kept separate from the pane so the same surface
 * can also render read-only media reopened from History.
 */
export function AttachedFilePreview({
  file,
  previewUrl,
  pageCount,
}: {
  file: File
  /** Object URL for an image; absent for every other kind. */
  previewUrl?: string
  pageCount?: number
}) {
  const kind = detectFileKind(file)

  return (
    <div className="flex-1 min-h-0 flex flex-col px-[18px] pb-3">
      <div className="flex-1 min-h-[180px] w-full overflow-hidden relative" style={{ borderRadius: 6, border: '1px solid var(--line)' }}>
        {kind === 'image' && previewUrl ? (
          <div className="relative h-full w-full min-h-[180px]">
            <img src={previewUrl} alt={file.name} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute bottom-0 inset-x-0 px-3 py-2 flex items-center gap-2" style={{ background: 'rgba(0,0,0,0.55)' }}>
              <ImageLucide size={14} className="text-white shrink-0" aria-hidden />
              <span className="text-[12px] font-medium text-white truncate flex-1">{file.name}</span>
              <span className="text-[10px] text-white/60 tabular-nums shrink-0">{(file.size / 1024 / 1024).toFixed(1)} MB</span>
            </div>
          </div>
        ) : (
          <div className="h-full min-h-[180px] flex flex-col items-center justify-center gap-3 p-6" style={{ background: 'var(--card)' }}>
            <DocumentBadge label={fileBadgeLabel(file)} />
            <div className="text-center">
              <div className="text-[13px] font-medium truncate max-w-[260px]" style={{ color: 'var(--ink)' }}>{file.name}</div>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between pt-2.5 shrink-0">
        <span className="font-mono text-[11px]" style={{ color: 'var(--muted-2)' }}>
          {(file.size / 1024 / 1024).toFixed(2)} / {FILE_MAX_MB} MB
        </span>
        <span className="font-mono text-[11px]" style={{ color: 'var(--muted-2)' }}>
          {kind === 'image' ? 'Image' : kind === 'pdf' ? 'PDF' : 'Document'}
          {typeof pageCount === 'number' ? ` · ${pageCount} pages` : ''}
        </span>
      </div>
    </div>
  )
}

export function DocumentBadge({ label }: { label: string }) {
  return (
    <div
      className="h-14 w-11 flex items-center justify-center"
      style={{ borderRadius: 6, background: 'var(--accent-soft)', color: 'var(--accent-deep)' }}
    >
      <span className="text-[10.5px] font-black tracking-widest">{label}</span>
    </div>
  )
}
